import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "timeline__label" }
const _hoisted_2 = {
  key: 0,
  class: "timeline__time-left"
}
const _hoisted_3 = { class: "time-left__background-line" }
const _hoisted_4 = { class: "time-left__time" }

import { computed, ref, watch, inject, onUnmounted } from "vue";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { LotSoldStatuses, LotStatuses, BiddingSteps } from "@/enum";
  import { trans } from "@/utils/trans";

  
export default /*#__PURE__*/_defineComponent({
  __name: 'TimeLine',
  setup(__props) {

  const auctionStore = useAuctionStore();
  const emitter = inject("emitter");
  const liveSettings = computed(() => auctionStore.liveSettings);
  const lots = computed(() => auctionStore.lots);
  const liveLot = computed(() => lots.value.find((item) => item.status === LotStatuses.LIVE));
  const endedLots = computed(() => lots.value.filter((item) => item.status === LotStatuses.ENDED));
  const isAuctionStarted = computed(() => liveLot.value?.id || endedLots.value.length);

  const activeLot = computed(() => auctionStore.activeLot);
  const soldStatus = computed(() => activeLot.value?.sold_status);
  const userId = computed(() => auctionStore.user?.id);
  const userBid = computed(() => (activeLot.value?.id ? auctionStore.user?.bids[activeLot.value.id] ?? 0 : 0));
  const currentStep = computed(() => (auctionStore.activeLot?.id ? auctionStore.activeLot.live_step : 0));

  const leaderId = computed(() => activeLot.value?.leader_id);
  const isLeader = computed(() => userId.value === leaderId.value);
  const isLotCompleted = computed(
    () =>
      activeLot.value?.sold_status === LotSoldStatuses.SOLD || activeLot.value?.sold_status === LotSoldStatuses.UNSOLD
  );

  let interval: number | undefined;
  const remainingTime = ref(0);
  const progressWidth = ref(100);
  const biddingDuration = computed(() => auctionStore.liveSettings?.bidding_duration ?? 10);
  const flashingDuration = computed(() => auctionStore.liveSettings?.warning_flashing_duration ?? 3);
  const isFlashing = ref(false);

  const bidStatusMessage = computed(() => {
    if (userBid.value > 0 && !auctionStore.user?.is_admin) {
      if (isLeader.value) {
        return isLotCompleted.value ? trans("live.bidding.you_won") : trans("live.bidding.leading");
      } else {
        return isLotCompleted.value ? "" : trans("live.bidding.outbid");
      }
    } else {
      if (currentStep.value === BiddingSteps.COMPLETE_LOT || isLotCompleted.value) {
        return "";
      }

      if (auctionStore.liveSettings?.next_lot_message) {
        return auctionStore.liveSettings.next_lot_message;
      } else {
        return trans("live.bidding.place_your_bids");
      }
    }
  });

  const timelineLabel = computed(() => {
    switch (currentStep.value) {
      case BiddingSteps.ONCE:
        return liveSettings.value?.once_message ?? "Once";
      case BiddingSteps.TWICE:
        return liveSettings.value?.twice_message ?? "Twice";
      case BiddingSteps.COMPLETE_LOT:
        return soldStatus.value === LotSoldStatuses.SOLD || soldStatus.value === LotSoldStatuses.UNSOLD
          ? trans("live.bidding.lot_" + soldStatus.value.toLowerCase())
          : "";
      default:
        return "";
    }
  });

  watch(
    () => auctionStore.resetTimeLineTrigger,
    () => {
      if (biddingDuration.value) {
        startCountdown(0);
      }
    }
  );

  watch(
    () => auctionStore.syncTimeLineTrigger,
    () => {
      syncCountdownWithLiveStep();
    }
  );

  watch(
    () => activeLot.value?.sold_status,
    (newValue) => {
      if (newValue === LotSoldStatuses.SOLD || newValue === LotSoldStatuses.UNSOLD) {
        isFlashing.value = false;
        stopCountdown();
      }
    }
  );

  onUnmounted(() => {
    if (interval) {
      clearInterval(interval);
      interval = undefined;
    }
  });

  const startCountdown = (initialElapsedTime = 0) => {
    isFlashing.value = false;
    if (interval) {
      clearInterval(interval);
    }

    const duration = biddingDuration.value;
    remainingTime.value = Math.max(duration - Math.floor(initialElapsedTime), 0);
    progressWidth.value = Math.min((initialElapsedTime / duration) * 100, 100);

    if (initialElapsedTime >= duration) {
      if (flashingDuration.value > 0 && flashingDuration.value >= remainingTime.value) {
        isFlashing.value = true;
      }

      return;
    }

    const startTime = Date.now();
    interval = setInterval(() => {
      const elapsedTime = initialElapsedTime + (Date.now() - startTime) / 1000;
      remainingTime.value = Math.max(duration - Math.floor(elapsedTime), 0);
      progressWidth.value = Math.min((elapsedTime / duration) * 100, 100);

      if (flashingDuration.value > 0 && flashingDuration.value >= remainingTime.value) {
        isFlashing.value = true;
      }

      if (elapsedTime >= duration) {
        clearInterval(interval);
      }
    }, 10);
  };

  const stopCountdown = () => {
    if (interval) {
      clearInterval(interval);
      interval = undefined;
    }

    remainingTime.value = 0;
    progressWidth.value = 100;
    isFlashing.value = false;
  };

  const syncCountdownWithLiveStep = () => {
    if (
        !activeLot.value?.id ||
        !biddingDuration.value ||
        currentStep.value === BiddingSteps.COMPLETE_LOT ||
        isLotCompleted.value
    ) {
      stopCountdown();
      return;
    }

    const liveStepUpdatedAtMs = getLiveStepUpdatedAtMs();

    if (!liveStepUpdatedAtMs) {
      startCountdown(0);
      return;
    }

    const elapsedTime = Math.max((auctionStore.getServerNowMs() - liveStepUpdatedAtMs) / 1000, 0);
    startCountdown(elapsedTime);
  };

  const getLiveStepUpdatedAtMs = () => {
    const rawValue = activeLot.value?.live_step_updated_at;

    if (!rawValue) {
      return null;
    }

    const timestamp = new Date(rawValue).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  };

return (_ctx: any,_cache: any) => {
  return (isAuctionStarted.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(['timeline', { winning: isLeader.value, flashing: isFlashing.value }])
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("span", null, _toDisplayString(bidStatusMessage.value), 1),
          _createTextVNode(" " + _toDisplayString(timelineLabel.value), 1)
        ]),
        (biddingDuration.value > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("span", {
                  class: "progress",
                  style: _normalizeStyle({ width: progressWidth.value + '%' })
                }, null, 4)
              ]),
              _createElementVNode("div", _hoisted_4, _toDisplayString(remainingTime.value > 0 ? remainingTime.value : ""), 1)
            ]))
          : _createCommentVNode("", true)
      ], 2))
    : _createCommentVNode("", true)
}
}

})